import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import countryList from 'react-select-country-list';
import Select from 'react-select';
import { Tooltip } from 'react-tooltip';
import validator from 'validator';
import cn from 'classnames';

import { BGHoverComponent } from '../../components/BGHoverComponent/BGHoverComponent';
import { MenuItems } from '../../types/MenuItems';
import { MenuPageWrapper } from '../../components/MenuPageWrapper/MenuPageWrapper';
import axios from '../../api/axios-sixtix';
import { getTranslation } from '../../constants/translations';
import { isValidEmail as emailValidator } from '../../helpers/emailValidator';

import logoNL from '../../assets/images/logo-orange.webp';
import logoCOM from '../../assets/images/logo-green.webp';
import { Icons } from '../../components/Icons/Icons';

import './Register.scss';
import { getLocalizedImg } from '../../helpers/getLocalizedImg';

const INITIAL_USER_DATA = {
  login_organization: '',
  login_country: {
    value: '',
    label: '',
  },
  contact_phone: '',
  username: '',
  password: '',
  password_confirm: '',
  referer: 'sixtix-register',
};

const PASSWORD_RULES = {
  minLength: 8,
  minLowercase: 1,
  minUppercase: 1,
  minNumbers: 1,
  minSymbols: 1,
};

export const Register = () => {
  const style = window.getComputedStyle(document.body as HTMLElement);
  const [userData, setUserData] = useState(INITIAL_USER_DATA);
  const [isTerms, setTerms] = useState(false);
  const [isPasswordValid, setPasswordValid] = useState(0);
  const [isSubmit, setSubmit] = useState(false);

  const options = useMemo(() => countryList().getData(), []);

  const t = getTranslation();

  const validatePassword = (password: string) => {
    if (validator.isStrongPassword(password, PASSWORD_RULES)) {
      setPasswordValid(1);
    } else {
      setPasswordValid(-1);
    }
  };

  const handleChangeUserInfo = (name: string, value: string) => {
    setSubmit(false);

    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'password') {
      validatePassword(value);
    }
  };

  const changeCountryHandler = (
    country: { value: string; label: string } | null,
  ) => {
    if (country) {
      setUserData((prev) => ({
        ...prev,
        login_country: country,
      }));
    }
  };

  const handleChangeTerms = () => {
    setTerms((prev) => !prev);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const {
      login_organization,
      login_country,
      contact_phone,
      username,
      password,
      password_confirm,
      referer,
    } = userData;

    setSubmit(true);

    if (
      !login_organization ||
      !login_country ||
      !contact_phone ||
      !username ||
      !password ||
      !password_confirm
    ) {
      toast.error(t.COMMON.formErrors['empty fields']);

      return;
    }

    if (
      !validator.isStrongPassword(password, PASSWORD_RULES) ||
      password !== password_confirm ||
      !emailValidator(username)
    ) {
      return;
    }

    const formData = new FormData();

    formData.append('login_organization', login_organization);
    formData.append('login_country', login_country.value);
    formData.append('contact_phone', contact_phone);
    formData.append('username', username);
    formData.append('password', password);
    formData.append('password_confirm', password_confirm);
    formData.append('referer', referer);

    const config = {
      headers: { 'content-type': 'multipart/form-data' },
    };

    try {
      const { data } = await axios.post('/ajax/register', formData, config);

      if (data.result) {
        setUserData(INITIAL_USER_DATA);
        setPasswordValid(0);
        toast.success('Succes!');
        setTerms(false);
      } else {
        toast.error(
          'Registratie mislukt, neem contact met ons op via support@sixtix.nl',
        );
      }
    } catch (error) {
      toast.error('Er is iets misgegaan. Probeer het later opnieuw.');
    } finally {
      setSubmit(false);
    }
  };

  useEffect(() => {
    setUserData(INITIAL_USER_DATA);
    setSubmit(false);
    setTerms(false);
    setPasswordValid(0);
  }, [window.location.hash]);

  return (
    <MenuPageWrapper
      id={MenuItems.REGISTER}
      color={style.getPropertyValue('--dark-blue')}
    >
      <div className="menu-page-wrapper__content">
        <h2 className="menu-page-wrapper__title">{t.REGISTER.mainTitle}</h2>

        <div className="menu-page-wrapper__text">
          <p dangerouslySetInnerHTML={{ __html: t.REGISTER.contentHTML }}></p>
        </div>
      </div>

      <article className="menu-page-wrapper__info-section register__info-section">
        {('ontouchstart' in window || navigator.maxTouchPoints > 0) && (
          <BGHoverComponent customLogoClassName="register__bg-logo" />
        )}

        <img
          className="menu-page-wrapper__logo"
          src={getLocalizedImg(logoNL, logoCOM)}
          alt="SixTix"
        />

        <form className="menu-page-wrapper__form" onSubmit={handleSubmit}>
          <label
            className={cn('label', {
              'label--error': !userData.username && isSubmit,
            })}
          >
            <input
              type="email"
              className="_blockLogoHover"
              placeholder={t.REGISTER.email}
              name="username"
              value={userData.username}
              onChange={({ target }) =>
                handleChangeUserInfo(target.name, target.value)
              }
            />

            {!emailValidator(userData.username) && !!userData.username && (
              <Icons
                name="alert"
                customClassName="label__icon"
                data-tooltip-id="email-validator"
              />
            )}

            <Tooltip
              id="email-validator"
              className="custom-tooltip"
              openOnClick={true}
            >
              <p>{t.COMMON.formErrors['Email is not valid']}</p>
            </Tooltip>
          </label>

          <div className="menu-page-wrapper__label-wrapper">
            <label
              className={cn('label', {
                'label--error': !userData.password && isSubmit,
              })}
            >
              <input
                type="password"
                className="_blockLogoHover"
                placeholder={t.REGISTER.password}
                name="password"
                value={userData.password}
                onChange={({ target }) =>
                  handleChangeUserInfo(target.name, target.value)
                }
              />
            </label>

            {isPasswordValid === -1 && !!userData.password && (
              <Icons
                name="alert"
                customClassName="label__icon"
                data-tooltip-id="password-rules"
              />
            )}

            <Tooltip
              id="password-rules"
              className="custom-tooltip"
              openOnClick={true}
            >
              <ul>
                <strong>{t.COMMON.passwordRules.title}</strong>

                {t.COMMON.passwordRules.rules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </Tooltip>

            {isPasswordValid === 1 && (
              <Icons
                name="checked"
                customClassName="label__icon"
                data-tooltip-id="password-rules"
              />
            )}
          </div>

          <label
            className={cn('label menu-page-wrapper__gap', {
              'label--error': !userData.password_confirm && isSubmit,
            })}
          >
            <input
              type="password"
              className="_blockLogoHover"
              placeholder={t.REGISTER.confirmPassword}
              name="password_confirm"
              value={userData.password_confirm}
              onChange={({ target }) =>
                handleChangeUserInfo(target.name, target.value)
              }
            />

            {userData.password !== userData.password_confirm &&
              !!userData.password_confirm &&
              isPasswordValid === 1 && (
                <Icons
                  name="alert"
                  customClassName="label__icon"
                  data-tooltip-id="password-confirm"
                />
              )}

            <Tooltip
              id="password-confirm"
              className="custom-tooltip"
              openOnClick={true}
            >
              <p>{t.COMMON['Passwords do not match']}</p>
            </Tooltip>

            {userData.password === userData.password_confirm &&
              !!userData.password_confirm &&
              isPasswordValid === 1 && (
                <Icons
                  name="checked"
                  customClassName="label__icon"
                  data-tooltip-id="password-confirm"
                />
              )}
          </label>

          <label
            className={cn('label', {
              'label--error': !userData.login_organization && isSubmit,
            })}
          >
            <input
              type="text"
              className="_blockLogoHover"
              placeholder={t.REGISTER.organization}
              name="login_organization"
              value={userData.login_organization}
              onChange={({ target }) =>
                handleChangeUserInfo(target.name, target.value)
              }
            />
          </label>

          <Select
            options={options}
            value={userData.login_country.value ? userData.login_country : null}
            onChange={changeCountryHandler}
            classNamePrefix="custom-select"
            className="custom-select"
            placeholder={t.REGISTER.country}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                outline:
                  !userData.login_country.value && isSubmit
                    ? `2px solid red`
                    : '',
                backgroundColor:
                  !userData.login_country.value && isSubmit
                    ? style.getPropertyValue('--error-input-bg')
                    : '#fff',
                border: 'none',
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                background:
                  !userData.login_country.value && isSubmit
                    ? 'transparent'
                    : '#fff',
              }),
              indicatorsContainer: (baseStyles) => ({
                ...baseStyles,
                backgroundColor:
                  !userData.login_country.value && isSubmit
                    ? 'transparent'
                    : '#fff',
              }),
            }}
          />

          <label
            className={cn('label menu-page-wrapper__gap', {
              'label--error': !userData.contact_phone && isSubmit,
            })}
          >
            <input
              type="tel"
              className="_blockLogoHover"
              placeholder={t.REGISTER.phone}
              name="contact_phone"
              value={userData.contact_phone}
              onChange={({ target }) =>
                handleChangeUserInfo(target.name, target.value)
              }
            />
          </label>

          <div className="checkbox">
            <input
              type="checkbox"
              id="terms"
              checked={isTerms}
              onChange={handleChangeTerms}
            />
            <label htmlFor="terms" className="_blockLogoHover">
              <svg viewBox="0,0,50,50">
                <path d="M5 30 L 20 45 L 45 5"></path>
              </svg>
            </label>
            <p>
              {t.REGISTER['I agree to the']}{' '}
              <a
                href="https://sixtix.nl/documents/SIXTIX_NL_Algemene_Voorwaarden_2025_NL.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="register__link _blockLogoHover"
              >
                {t.COMMON['Terms and Conditions']}
              </a>
            </p>
          </div>

          <button
            className="register__submit menu-submit _blockLogoHover"
            type="submit"
            disabled={!isTerms}
          >
            {t.REGISTER.apply}
          </button>
        </form>
      </article>
    </MenuPageWrapper>
  );
};
